import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { BarChart2, X, ClipboardCopy, Check, FileText, Loader2 } from "lucide-react";
import axios from "axios";
// import { VICTORY_PHRASES } from "./constants";
import { GameStatsContent } from "./GameStatsModal";
import { diagnosticsEnabled } from "@/utils/diagnostics";
import { track } from "@/utils/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VictoryModal({ open, winner, game, gameId, gameStats, myRole = null }) {
  // const [phrase] = useState(() => VICTORY_PHRASES[Math.floor(Math.random() * VICTORY_PHRASES.length)]);
  const [showStats, setShowStats] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [botReport, setBotReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const isBotVsBot = game?.bot_role === "both";
  // Diagnostics report buttons are feature-flagged — hidden in prod unless the
  // viewer enabled them (visit any page with ?diag=1). See utils/diagnostics.js.
  const showDiagnostics = diagnosticsEnabled();

  useEffect(() => {
    if (!open || !winner || !game) return;
    const modeTag = isBotVsBot ? "bot_vs_bot" : isBot ? "vs_bot" : "h2h";
    track(`game_completed_${modeTag}`, {
      game_mode: game.game_mode ?? "unknown",
      winner_side: winner.winner,
      reason: winner.reason,
      inning: game.state?.inning ?? 1,
      innings_total: game.innings_total ?? 9,
      home_score: game.state?.home_score ?? 0,
      away_score: game.state?.away_score ?? 0,
      has_deck_draft: !!game.draft_enabled,
      has_player_draft: !!game.player_draft_enabled,
    });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => {
      if (e.key === "Enter") handleNewGame();
      if (e.key === "Escape") handleHome();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!winner || !game) return null;

  const winningTeam = winner.winner === "home" ? game.home_team : game.away_team;
  const losingTeam = winner.winner === "home" ? game.away_team : game.home_team;
  const winnerScore = winner.winner === "home" ? game.state.home_score : game.state.away_score;
  const loserScore = winner.winner === "home" ? game.state.away_score : game.state.home_score;
  const isOnline = game.game_mode === "online";
  const isBot = !!game.bot_role;

  const getWinDescription = () => {
    switch (winner.reason) {
      case "walk-off": return "IT'S A WALK-OFF!";
      case "walk-off-extra": return "GRIT, DETERMINATION, HEART--THIS GAME HAD IT ALL!";
      case "extra-innings": return "CONGRATULATIONS, IT'S FINALLY OVER!";
      case "regulation":
        // An away win is decided only once the home team has batted and failed
        // in the bottom of the final inning — the full-distance ending this line
        // is written for. Home wins end the moment the lead holds, so they stay
        // on the flat "FINAL".
        //
        // Keyed on the winning side rather than on `reason` because `reason`
        // used to say "extra-innings" for these games (a completed bottom of the
        // 9th flips state to inning 10 / top, which the away-win branch read as
        // extras). Fixing that field would have silently retired this line, so
        // the display is pinned here deliberately and covered by a test.
        return winner.winner === "away" ? "CONGRATULATIONS, IT'S FINALLY OVER!" : "FINAL";
      default: return "VICTORY!";
    }
  };

  // New Game: save current game settings to sessionStorage so the setup page can
  // restore them, then navigate to the appropriate setup page. Rosters start fresh.
  const handleNewGame = () => {
    try {
      const s = {
        innings_total: game.innings_total ?? 9,
        ballpark: game.ballpark ?? "",
        excluded_rarities: game.excluded_rarities ?? ["junk_wax"],
        chip_max: game.chip_max ?? 4,
        pitch_mod: game.pitch_mod ?? 2,
        hand_size: game.hand_size ?? 5,
        require_chip_match_for_hr: game.require_chip_match_for_hr ?? true,
        depleting_chips: game.depleting_chips ?? true,
        chip_arsenal: game.chip_arsenal ?? false,
        chip_arsenal_infinite: game.chip_arsenal_infinite ?? 1,
        chip_arsenal_modifiers: game.chip_arsenal_modifiers ?? [1, 1, 1, 1],
        chip_arsenal_fb_count: game.chip_arsenal_fb_count ?? 8,
        chip_arsenal_ch_count: game.chip_arsenal_ch_count ?? 5,
        chip_arsenal_cb_count: game.chip_arsenal_cb_count ?? 3,
        chip_arsenal_sl_count: game.chip_arsenal_sl_count ?? 2,
        continuous_draw: game.continuous_draw ?? false,
        heat_map: game.heat_map ?? false,
        heat_map_overlap_bonus: game.heat_map_overlap_bonus ?? 1,
        heat_map_chip_bonus: game.heat_map_chip_bonus ?? 1,
        heat_map_overhang_threshold: game.heat_map_overhang_threshold ?? 20,
        heat_map_zone_width_pct: game.heat_map_zone_width_pct ?? 100,
        same_raw_tie_is_ball: game.same_raw_tie_is_ball ?? true,
        modifier_tie_is_ball: game.modifier_tie_is_ball ?? true,
        any_raw_tie_is_ball: game.any_raw_tie_is_ball ?? false,
        called_ball_tie_is_strike: game.called_ball_tie_is_strike ?? false,
        bluff_values: game.bluff_values ?? false,
        unlimited_stacking: game.unlimited_stacking ?? false,
        pitch_clock_secs: game.pitch_clock_secs ?? null,
        pitch_pitcher_secs: game.pitch_pitcher_secs ?? null,
        pitch_batter_secs: game.pitch_batter_secs ?? null,
        fielding_clock_secs: game.fielding_clock_secs ?? null,
        steal_clock_secs: game.steal_clock_secs ?? null,
        hand_refresh_secs: game.hand_refresh_secs ?? null,
        pickoff_select_secs: game.pickoff_select_secs ?? null,
        card_modal_secs: game.card_modal_secs ?? null,
        swap_spit_secs: game.swap_spit_secs ?? null,
        draft_pick_secs: game.draft_pick_secs ?? null,
        player_draft_pick_secs: game.player_draft_pick_secs ?? null,
      };
      sessionStorage.setItem("junkball_new_game_settings", JSON.stringify(s));
    } catch {}
    if (isBotVsBot) { window.location.href = "/bot-vs-bot-setup"; return; }
    window.location.href = isBot ? "/bot-setup" : "/online-setup";
  };

  // Home: return to landing page without saving settings.
  const handleHome = () => { window.location.href = "/"; };

  const handleToggleReport = async () => {
    if (showReport) { setShowReport(false); setShowStats(false); return; }
    setShowStats(false);
    setShowReport(true);
    if (botReport) return;
    setReportLoading(true);
    try {
      const res = await axios.get(`${API}/games/${gameId}/bot-report`);
      setBotReport(res.data);
    } catch {
      toast.error("Report not ready yet — the game may still be finishing up");
      setShowReport(false);
    } finally {
      setReportLoading(false);
    }
  };

  const handleCopy = () => {
    const text = botReport?.llm_context || "";
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => toast.error("Copy failed — select the text manually"));
  };

  return (
    <Dialog open={open}>
      <DialogContent
        className={`bg-gradient-to-b from-slate-900 via-slate-900 to-amber-950/30 border-amber-500/30 [&>button]:hidden overflow-hidden transition-all duration-300 ${(showStats || showReport) ? 'max-w-6xl' : 'max-w-md'}`}
      >
        <VisuallyHidden><DialogTitle>Game Over — {winningTeam?.name} wins</DialogTitle></VisuallyHidden>
        <div className="flex items-center gap-0">
          {/* Victory panel */}
          <div className="flex-shrink-0 w-[380px] py-4 text-center space-y-4">
            {/* Logo */}
            <div className="flex justify-center">
              <img src="/favicons48x48.png" alt="Junk Ball" className="w-12 h-12 object-contain" />
            </div>

            {/* Win type badge */}
            <div className="inline-flex items-center px-4 py-1.5 bg-amber-500/20 border border-amber-500/30 rounded-full">
              <span className="text-xs font-bold tracking-widest text-amber-400">{getWinDescription()}</span>
            </div>

            {/* Winner */}
            <div className="space-y-1">
              <div className="text-4xl font-black text-white tracking-wide">{winningTeam.name}</div>
              <div className="text-sm text-amber-400/80 font-medium">WINS!</div>
            </div>

            {/* Score */}
            <div className="flex items-center justify-center gap-4 py-3">
              <div className="text-center">
                <div className="text-5xl font-black text-amber-400">{winnerScore}</div>
                <div className="text-xs text-slate-400 mt-1">{winningTeam.name}</div>
              </div>
              <div className="text-2xl text-slate-600 font-light">—</div>
              <div className="text-center opacity-60">
                <div className="text-5xl font-black text-slate-400">{loserScore}</div>
                <div className="text-xs text-slate-500 mt-1">{losingTeam.name}</div>
              </div>
            </div>

            {/* Phrase */}

            {(() => {
              // Server's flip_half_inning bumps state.inning to N+1 even on the play
              // that ends the game (top of N+1, never played). Display the actual last
              // inning played: subtract one when the game ended on a top-half flip.
              const stateInn = game.state.inning;
              const lastPlayedInning = (game.state.half === "top" && stateInn > 1) ? stateInn - 1 : stateInn;
              return (
                <div className="text-xs text-slate-500">
                  Final score after {lastPlayedInning} inning{lastPlayedInning !== 1 ? 's' : ''}
                </div>
              );
            })()}

            {/* Actions */}
            <div className="flex flex-col gap-2 pt-2">
              <div className="grid grid-cols-2 gap-2">
                <Button onClick={handleHome} variant="outline"
                  className="h-11 border-slate-600 text-slate-300 hover:bg-slate-800 font-bold">
                  Home
                </Button>
                <Button onClick={handleNewGame}
                  className="h-11 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-black font-bold">
                  New Game
                </Button>
              </div>
              <Button onClick={() => { setShowStats(s => !s); setShowReport(false); }} variant="ghost"
                className="h-9 text-slate-400 hover:text-white text-sm flex items-center gap-2">
                <BarChart2 size={14} />
                {showStats ? "Hide Box Score" : "View Box Score"}
              </Button>
              {showDiagnostics && (
                <Button onClick={handleToggleReport} variant="ghost"
                  className="h-9 text-emerald-500/70 hover:text-emerald-400 text-sm flex items-center gap-2">
                  {reportLoading ? <Loader2 size={14} className="animate-spin" /> : <FileText size={14} />}
                  {showReport
                    ? (isBotVsBot ? "Hide Bot Report" : "Hide Diagnostics")
                    : (isBotVsBot ? "Bot Report" : "Diagnostics Report")}
                </Button>
              )}
            </div>
          </div>

          {/* Stats panel — slides in from the right */}
          <AnimatePresence>
            {showStats && gameStats && (
              <motion.div
                initial={{ width: 0, opacity: 0 }}
                animate={{ width: 600, opacity: 1 }}
                exit={{ width: 0, opacity: 0 }}
                transition={{ duration: 0.25, ease: "easeInOut" }}
                className="overflow-hidden"
              >
                <div className="w-[600px] border-l border-slate-700/50 pl-4 flex flex-col self-stretch justify-center">
                  <div className="flex items-center justify-between mb-3 py-1">
                    <span className="text-xs font-bold text-slate-500 tracking-wider uppercase">Box Score</span>
                    <button onClick={() => setShowStats(false)}
                      className="p-1 rounded hover:bg-slate-800 text-slate-500 hover:text-white transition-colors">
                      <X size={14} />
                    </button>
                  </div>
                  <div className="overflow-y-auto flex-1 max-h-[70vh] pr-1">
                    <GameStatsContent game={game} gameStats={gameStats} />
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Bot Report panel — slides in from the right */}
          <AnimatePresence>
            {showReport && (
              <motion.div
                initial={{ width: 0, opacity: 0 }}
                animate={{ width: 520, opacity: 1 }}
                exit={{ width: 0, opacity: 0 }}
                transition={{ duration: 0.25, ease: "easeInOut" }}
                className="overflow-hidden"
              >
                <div className="w-[520px] border-l border-slate-700/50 pl-4 flex flex-col self-stretch py-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-bold text-emerald-500/80 tracking-wider uppercase">
                      {isBotVsBot ? "Bot Diagnostic Report" : "Diagnostics Snapshot"}
                    </span>
                    <div className="flex items-center gap-2">
                      {botReport && (
                        <button onClick={handleCopy}
                          className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-emerald-600/20 hover:bg-emerald-600/40 text-emerald-400 text-xs font-bold transition-colors">
                          {copied ? <Check size={12} /> : <ClipboardCopy size={12} />}
                          {copied ? "Copied!" : "Copy"}
                        </button>
                      )}
                      <button onClick={() => setShowReport(false)}
                        className="p-1 rounded hover:bg-slate-800 text-slate-500 hover:text-white transition-colors">
                        <X size={14} />
                      </button>
                    </div>
                  </div>

                  {reportLoading && (
                    <div className="flex items-center justify-center flex-1 gap-2 text-slate-500">
                      <Loader2 size={16} className="animate-spin" />
                      <span className="text-sm">Loading report…</span>
                    </div>
                  )}

                  {botReport && !reportLoading && (
                    <>
                      {/* Summary chips — the snapshot tracks only audits + card
                          counts (no per-pitch data); bot-vs-bot has the full set. */}
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {(botReport.kind === "diagnostics_snapshot"
                          ? [
                              { label: "Audit issues", value: botReport.summary?.total_audit_issues, warn: botReport.summary?.total_audit_issues > 0 },
                              { label: "Cards played", value: botReport.summary?.total_cards_submitted },
                            ]
                          : [
                              { label: "Phase resets", value: botReport.summary?.phase_resets, warn: botReport.summary?.phase_resets > 0 },
                              { label: "Exceptions", value: botReport.summary?.exceptions, warn: botReport.summary?.exceptions > 0 },
                              { label: "Stuck phases", value: botReport.summary?.stuck_phases, warn: botReport.summary?.stuck_phases > 0 },
                              { label: "Pitches", value: botReport.summary?.total_pitches_logged },
                              { label: "Cards played", value: botReport.summary?.total_cards_submitted },
                              { label: "Problem cards", value: botReport.summary?.unique_problem_cards, warn: botReport.summary?.unique_problem_cards > 0 },
                              { label: "Audit issues", value: botReport.summary?.total_audit_issues, warn: botReport.summary?.total_audit_issues > 0 },
                            ]
                        ).map(({ label, value, warn }) => (
                          <span key={label} className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${warn ? "bg-red-500/10 border-red-500/30 text-red-400" : "bg-slate-800 border-slate-700 text-slate-400"}`}>
                            {label}: {value ?? "—"}
                          </span>
                        ))}
                      </div>

                      <p className="text-[10px] text-slate-600 mb-2">
                        Paste the text below into Claude (or any LLM) for diagnosis.
                      </p>

                      <pre className="flex-1 overflow-y-auto max-h-[55vh] text-[11px] leading-relaxed text-slate-300 whitespace-pre-wrap break-words bg-slate-950/60 rounded-xl border border-slate-800 p-3 font-mono select-all cursor-text">
                        {botReport.llm_context}
                      </pre>
                    </>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </DialogContent>
    </Dialog>
  );
}
